import { useState, useRef, type ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import FunProgress from '../ui/FunProgress';
import {
  Upload, Link, AlertCircle, CheckCircle, FileAudio, Scissors,
  Minimize2, Video as VideoIcon, Sparkles, Image as ImageIcon,
  Download, Film, Music, Zap, Clock, HardDrive, Play,
  Instagram, Youtube, Smartphone, Monitor,
} from 'lucide-react';

/* ─────────────────────────── types ─────────────────────────────── */
type ActiveTab  = 'extract' | 'tools' | 'audio';
type InputMode  = 'file' | 'url';
type VideoMode  = 'compress' | 'trim' | 'enhance' | 'gif';
type Quality    = 'low' | 'medium' | 'high';

/* ─────────────── Social-media output presets ────────────────────── */
interface Preset {
  id: string;
  label: string;
  sublabel: string;
  icon: ReactNode;
  width?: string;
  fps?: string;
  quality?: Quality;
  note?: string;
}

const GIF_PRESETS: Preset[] = [
  { id: 'ig-story',  label: 'IG Story',   sublabel: '480px · 15fps', icon: <Instagram size={14} />, width: '480',  fps: '15' },
  { id: 'tiktok',    label: 'TikTok',     sublabel: '640px · 24fps', icon: <Smartphone size={14} />, width: '640', fps: '24' },
  { id: 'twitter',   label: 'Twitter GIF',sublabel: '480px · 12fps', icon: <Monitor size={14} />,   width: '480',  fps: '12' },
  { id: 'hd',        label: 'HD',         sublabel: '1080px · 30fps',icon: <Film size={14} />,       width: '1080', fps: '30' },
];

const COMPRESS_PRESETS: Preset[] = [
  { id: 'web',       label: 'Web',        sublabel: 'Balanced quality',  icon: <Monitor size={14} />,    quality: 'medium' },
  { id: 'social',    label: 'Social',     sublabel: 'Fast upload',       icon: <Instagram size={14} />,  quality: 'low'    },
  { id: 'archive',   label: 'Archive',    sublabel: 'Best quality',      icon: <HardDrive size={14} />,  quality: 'high'   },
  { id: 'yt-short',  label: 'YT Short',   sublabel: 'Optimized',         icon: <Youtube size={14} />,    quality: 'medium' },
];

const AUDIO_FORMATS = ['mp3', 'wav', 'aac', 'ogg', 'flac'] as const;
const BITRATES      = ['320', '256', '192', '128'] as const;

/* ─────────────────────────── helpers ───────────────────────────── */
function formatBytes(b: number) {
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
}

/* ═══════════════════════ Component ════════════════════════════════ */
const VideoConverter = () => {
  const [activeTab,   setActiveTab]   = useState<ActiveTab>('extract');
  const [inputMode,   setInputMode]   = useState<InputMode>('file');

  const [videoFile,   setVideoFile]   = useState<File | null>(null);
  const [videoUrl,    setVideoUrl]    = useState('');
  const [audioFile,   setAudioFile]   = useState<File | null>(null);
  const [dragging,    setDragging]    = useState(false);

  const [videoMode,   setVideoMode]   = useState<VideoMode>('compress');
  const [quality,     setQuality]     = useState<Quality>('medium');
  const [startTime,   setStartTime]   = useState('00:00:00');
  const [endTime,     setEndTime]     = useState('00:00:10');
  const [gifFps,      setGifFps]      = useState('15');
  const [gifWidth,    setGifWidth]    = useState('480');
  const [audioFormat, setAudioFormat] = useState('mp3');
  const [audioBitrate,setAudioBitrate]= useState('320');

  const [converting,  setConverting]  = useState(false);
  const [error,       setError]       = useState<string | null>(null);
  const [success,     setSuccess]     = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [progress,    setProgress]    = useState(0);
  const [progStatus,  setProgStatus]  = useState('');

  const dropRef = useRef<HTMLDivElement>(null);

  const reset = () => {
    setError(null); setSuccess(null); setDownloadUrl(null);
    setProgress(0); setProgStatus('');
  };

  /* ── Drag & Drop ─────────────────────────────────────────── */
  const onDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer.files[0];
    if (!file) return;
    if (activeTab === 'audio') {
      setAudioFile(file);
    } else {
      setVideoFile(file);
    }
    reset();
  };

  /* ── Presets ─────────────────────────────────────────────── */
  const applyGifPreset = (p: Preset) => { setGifWidth(p.width!); setGifFps(p.fps!); };
  const applyCompressPreset = (p: Preset) => { setQuality(p.quality!); };

  /* ── Convert ─────────────────────────────────────────────── */
  const handleConvert = async () => {
    reset();
    if ((activeTab === 'extract' || activeTab === 'tools') && inputMode === 'file' && !videoFile)
      return setError('Please select a video file first');
    if ((activeTab === 'extract' || activeTab === 'tools') && inputMode === 'url' && !videoUrl)
      return setError('Please enter a video URL');
    if (activeTab === 'audio' && !audioFile)
      return setError('Please select an audio file');

    setConverting(true);

    try {
      let endpoint = '';
      const fd = new FormData();

      if (activeTab === 'extract') {
        endpoint = '/api/convert/video-to-mp3/stream';
        if (videoFile) fd.append('file', videoFile);
      } else if (activeTab === 'tools') {
        const modeMap: Record<VideoMode, string> = {
          compress: '/api/convert/video/compress/stream',
          trim:     '/api/convert/video/trim/stream',
          enhance:  '/api/convert/video/enhance/stream',
          gif:      '/api/convert/video/gif/stream',
        };
        endpoint = modeMap[videoMode];
        if (videoMode === 'compress') fd.append('quality', quality);
        if (videoMode === 'trim') { fd.append('startTime', startTime); fd.append('endTime', endTime); }
        if (videoMode === 'gif')  { fd.append('fps', gifFps); fd.append('width', gifWidth); }
        if (videoFile) fd.append('file', videoFile);
      } else {
        endpoint = '/api/convert/audio-to-audio/stream';
        if (audioFile) fd.append('file', audioFile);
      }

      fd.append('format', audioFormat);
      fd.append('bitrate', audioBitrate);

      /* URL mode — download first */
      if (inputMode === 'url') {
        // For YouTube URLs, download directly via stream endpoint
        if (videoUrl.includes('youtube.com') || videoUrl.includes('youtu.be')) {
          const resp = await fetch('/api/tools/youtube/stream', {
            method: 'POST', headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url: videoUrl, format: activeTab === 'extract' ? 'audio' : 'video', quality: 'best' }),
          });
          const reader = resp.body?.getReader();
          const decoder = new TextDecoder();
          if (reader) {
            while (true) {
              const { done, value } = await reader.read();
              if (done) break;
              for (const line of decoder.decode(value).split('\n')) {
                if (!line.startsWith('data: ')) continue;
                try {
                  const d = JSON.parse(line.slice(6));
                  if (d.type === 'progress') { setProgress(d.percent || 0); setProgStatus(d.status || 'Downloading...'); }
                  else if (d.type === 'complete') {
                    setProgress(100); setProgStatus('Done!');
                    setDownloadUrl(d.downloadUrl);
                    setSuccess('Downloaded successfully!');
                  } else if (d.type === 'error') { setError(d.error); }
                } catch {}
              }
            }
          }
          setConverting(false);
          return;
        }

        // For other URLs, download then convert
        setProgStatus('Downloading from URL...');
        setProgress(20);
        const dlResp = await fetch('/api/tools/download', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url: videoUrl }),
        });
        const dlData = await dlResp.json();
        if (!dlData.success) throw new Error(dlData.error || 'Failed to download video');

        setProgress(50); setProgStatus('Converting...');
        const route = activeTab === 'extract'
          ? '/api/convert/video-to-mp3-from-storage'
          : '/api/convert/video/compress-from-storage';

        const resp = await fetch(route, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ filename: dlData.filename, quality: 'low' }),
        });
        const data = await resp.json();
        if (data.success) { setProgress(100); setProgStatus('Done!'); setDownloadUrl(`${data.downloadUrl}`); setSuccess('Processed successfully!'); }
        else throw new Error(data.error);
        setConverting(false);
        return;
      }

      /* Upload with progress tracking + SSE streaming via fetch ReadableStream */
      setProgStatus('Uploading...');

      // Use fetch with ReadableStream to handle SSE properly
      // XHR doesn't work with SSE because it buffers the full response
      const resp = await fetch(endpoint, { method: 'POST', body: fd });

      if (!resp.ok) {
        const e = await resp.json().catch(() => ({ error: 'Upload failed' }));
        throw new Error(e.error || 'Upload failed');
      }

      const reader = resp.body?.getReader();
      const decoder = new TextDecoder();
      if (!reader) throw new Error('No response stream');

      setProgStatus('Converting...');
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        for (const line of decoder.decode(value).split('\n')) {
          if (!line.startsWith('data: ')) continue;
          try {
            const d = JSON.parse(line.slice(6));
            if (d.type === 'progress') {
              setProgress(d.percent || 0);
              setProgStatus(d.status || 'Converting...');
            } else if (d.type === 'complete') {
              setProgress(100);
              setProgStatus('Done!');
              setDownloadUrl(`${d.downloadUrl}`);
              setSuccess(d.message || 'Conversion complete!');
            } else if (d.type === 'error') {
              setError(d.error);
            }
          } catch { /* ignore parse errors */ }
        }
      }
    } catch (err: any) {
      setError(err.message || 'Network error. Is the server running?');
    } finally {
      setConverting(false);
    }
  };

  /* ── Derived ─────────────────────────────────────────────── */
  const activeFile    = activeTab === 'audio' ? audioFile : videoFile;
  const hasInput      = inputMode === 'url' ? !!videoUrl : !!activeFile;
  const isVideoTab    = activeTab === 'extract' || activeTab === 'tools';

  const tabIcon: Record<ActiveTab, ReactNode> = {
    extract: <FileAudio size={16} />,
    tools:   <VideoIcon size={16} />,
    audio:   <Music size={16} />,
  };

  return (
    <div className="space-y-6">
      {/* ── Hero ──────────────────────────────────────────── */}
      <div className="tool-hero">
        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-[#216974] to-[#54c4b6] flex items-center justify-center shadow-lg shadow-[#216974]/30">
                <Film size={16} className="text-white" />
              </div>
              <span className="text-xs font-semibold text-[#216974] bg-[#54c4b6]/15 px-2.5 py-1 rounded-full">
                Media Studio
              </span>
            </div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">
              Video &amp; Audio Converter
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              Professional-grade conversion, compression, and audio extraction for any workflow.
            </p>
          </div>
          {activeFile && (
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="hidden md:flex items-center gap-2 glass-card rounded-2xl px-4 py-2.5"
            >
              <Play size={14} className="text-[#216974]" />
              <div className="text-right">
                <p className="text-xs font-semibold text-gray-700 truncate max-w-[140px]">{activeFile.name}</p>
                <p className="text-[10px] text-gray-400">{formatBytes(activeFile.size)}</p>
              </div>
            </motion.div>
          )}
        </div>
      </div>

      <Card>
        {/* ── Tabs ────────────────────────────────────────── */}
        <div className="tab-pill-group mb-6">
          {(['extract', 'tools', 'audio'] as ActiveTab[]).map((tab) => (
            <button
              key={tab}
              onClick={() => { setActiveTab(tab); reset(); }}
              className={`tab-pill flex-1 capitalize ${activeTab === tab ? 'active' : ''}`}
            >
              {tabIcon[tab]}
              <span className="hidden sm:inline">
                {tab === 'extract' ? 'Extract Audio' : tab === 'tools' ? 'Video Tools' : 'Audio Converter'}
              </span>
              <span className="sm:hidden">
                {tab === 'extract' ? 'Extract' : tab === 'tools' ? 'Video' : 'Audio'}
              </span>
            </button>
          ))}
        </div>

        {/* ── Input mode (file / url) ──────────────────────── */}
        {isVideoTab && (
          <div className="tab-pill-group mb-5">
            <button onClick={() => { setInputMode('file'); reset(); }} className={`tab-pill flex-1 ${inputMode === 'file' ? 'active' : ''}`}>
              <Upload size={14} /> Upload File
            </button>
            <button
              onClick={() => { setInputMode('url'); reset(); if (activeTab === 'tools') { setVideoMode('compress'); setQuality('low'); } }}
              className={`tab-pill flex-1 ${inputMode === 'url' ? 'active' : ''}`}
            >
              <Link size={14} /> From URL
            </button>
          </div>
        )}

        <div className="space-y-5">
          {/* ── Drop zone ──────────────────────────────────── */}
          {isVideoTab ? (
            inputMode === 'file' ? (
              <div
                ref={dropRef}
                className={`drop-zone p-8 text-center ${dragging ? 'dragging' : ''} ${videoFile ? 'has-file' : ''}`}
                onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={onDrop}
                onClick={() => document.getElementById('video-upload')?.click()}
              >
                <input
                  type="file" id="video-upload" accept="video/*" className="hidden"
                  onChange={(e) => { if (e.target.files) { setVideoFile(e.target.files[0]); reset(); } }}
                />
                <motion.div
                  animate={dragging ? { scale: 1.1 } : videoFile ? { scale: 1 } : { scale: 1 }}
                  className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center"
                  style={{ background: videoFile ? 'rgba(124,58,237,0.12)' : 'rgba(241,243,249,0.8)' }}
                >
                  {videoFile
                    ? <VideoIcon size={26} className="text-[#216974]" />
                    : <Upload size={26} className="text-gray-400" />
                  }
                </motion.div>
                {videoFile ? (
                  <div>
                    <p className="font-semibold text-gray-800 text-sm">{videoFile.name}</p>
                    <p className="text-xs text-gray-400 mt-1">{formatBytes(videoFile.size)} · Click to change</p>
                  </div>
                ) : (
                  <div>
                    <p className="font-semibold text-gray-700">Drop video here or click to browse</p>
                    <p className="text-xs text-gray-400 mt-1">MP4, AVI, MOV, MKV, WebM · Up to 500 MB</p>
                  </div>
                )}
              </div>
            ) : (
              <Input
                placeholder="https://example.com/video.mp4"
                value={videoUrl} onChange={(e) => setVideoUrl(e.target.value)}
                leftIcon={<Link size={16} />}
                hint="Paste a direct video URL to download and process"
              />
            )
          ) : (
            <div
              className={`drop-zone p-8 text-center ${dragging ? 'dragging' : ''} ${audioFile ? 'has-file' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => document.getElementById('audio-upload')?.click()}
            >
              <input
                type="file" id="audio-upload" accept="audio/*" className="hidden"
                onChange={(e) => { if (e.target.files) { setAudioFile(e.target.files[0]); reset(); } }}
              />
              <div className={`w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center ${audioFile ? 'bg-[#216974]' : 'bg-gray-100'}`}>
                <FileAudio size={26} className={audioFile ? 'text-[#216974]' : 'text-gray-400'} />
              </div>
              {audioFile ? (
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{audioFile.name}</p>
                  <p className="text-xs text-gray-400 mt-1">{formatBytes(audioFile.size)} · Click to change</p>
                </div>
              ) : (
                <div>
                  <p className="font-semibold text-gray-700">Drop audio file or click to browse</p>
                  <p className="text-xs text-gray-400 mt-1">MP3, WAV, AAC, OGG, FLAC supported</p>
                </div>
              )}
            </div>
          )}

          {/* ── Video tool mode tabs ────────────────────────── */}
          <AnimatePresence>
            {activeTab === 'tools' && hasInput && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="space-y-5"
              >
                <div className="tab-pill-group">
                  {([
                    { id: 'compress', label: 'Compress',   icon: <Minimize2 size={14} /> },
                    { id: 'trim',     label: 'Trim',        icon: <Scissors size={14} />,   fileOnly: true },
                    { id: 'enhance',  label: 'AI Enhance',  icon: <Sparkles size={14} />,   fileOnly: true },
                    { id: 'gif',      label: 'To GIF',      icon: <ImageIcon size={14} />,  fileOnly: true },
                  ] as { id: VideoMode; label: string; icon: ReactNode; fileOnly?: boolean }[])
                    .filter((m) => !m.fileOnly || inputMode === 'file')
                    .map((m) => (
                      <button
                        key={m.id}
                        onClick={() => setVideoMode(m.id)}
                        className={`tab-pill flex-1 ${videoMode === m.id ? 'active' : ''}`}
                      >
                        {m.icon} <span className="hidden sm:inline">{m.label}</span>
                      </button>
                    ))
                  }
                </div>

                {/* Compress presets */}
                {videoMode === 'compress' && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                    <p className="section-label">Output Preset</p>
                    <div className="grid grid-cols-4 gap-2">
                      {COMPRESS_PRESETS.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => applyCompressPreset(p)}
                          className={`preset-card ${quality === p.quality ? 'active' : ''}`}
                        >
                          <span className="text-gray-500">{p.icon}</span>
                          <span className="text-xs font-semibold text-gray-700">{p.label}</span>
                          <span className="text-[10px] text-gray-400">{p.sublabel}</span>
                        </button>
                      ))}
                    </div>
                    <div className="quality-selector">
                      {(['low', 'medium', 'high'] as Quality[]).map((q) => (
                        <button
                          key={q}
                          onClick={() => inputMode !== 'url' && setQuality(q)}
                          disabled={inputMode === 'url' && q !== 'low'}
                          className={`quality-btn capitalize ${quality === q ? 'active' : ''}`}
                        >
                          {q === 'low' ? 'Low' : q === 'medium' ? 'Balanced' : 'High'}
                        </button>
                      ))}
                    </div>
                  </motion.div>
                )}

                {/* Trim */}
                {videoMode === 'trim' && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-3">
                    <p className="section-label">Time Range</p>
                    <div className="grid grid-cols-2 gap-4">
                      <Input label="Start Time" placeholder="00:00:00" value={startTime} onChange={(e) => setStartTime(e.target.value)} leftIcon={<Clock size={14} />} className="font-mono" />
                      <Input label="End Time"   placeholder="00:00:10" value={endTime}   onChange={(e) => setEndTime(e.target.value)}   leftIcon={<Clock size={14} />} className="font-mono" />
                    </div>
                  </motion.div>
                )}

                {/* Enhance */}
                {videoMode === 'enhance' && (
                  <motion.div
                    initial={{ opacity: 0 }} animate={{ opacity: 1 }}
                    className="flex gap-4 items-start p-4 rounded-2xl border border-[#54c4b6] bg-gradient-to-br from-[#216974]/80 to-[#216974]/60"
                  >
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#216974] to-[#216974] flex items-center justify-center shadow-md shadow-[#216974]/40 shrink-0">
                      <Sparkles size={16} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-[#216974]">AI-Style Quality Enhancement</p>
                      <p className="text-xs text-[#216974]/80 mt-1 leading-relaxed">
                        Upscales to 1080p, applies high-quality denoising (unsharp masking), and restores
                        edges with smart sharpening. Ideal for grainy or compressed footage.
                      </p>
                    </div>
                  </motion.div>
                )}

                {/* GIF */}
                {videoMode === 'gif' && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
                    <p className="section-label">GIF Presets</p>
                    <div className="grid grid-cols-4 gap-2 mb-3">
                      {GIF_PRESETS.map((p) => (
                        <button
                          key={p.id}
                          onClick={() => applyGifPreset(p)}
                          className={`preset-card ${gifWidth === p.width && gifFps === p.fps ? 'active' : ''}`}
                        >
                          <span className="text-gray-500">{p.icon}</span>
                          <span className="text-xs font-semibold text-gray-700">{p.label}</span>
                          <span className="text-[10px] text-gray-400">{p.sublabel}</span>
                        </button>
                      ))}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <Input label="FPS"         type="number" value={gifFps}   onChange={(e) => setGifFps(e.target.value)} />
                      <Input label="Width (px)"  type="number" value={gifWidth} onChange={(e) => setGifWidth(e.target.value)} />
                    </div>
                  </motion.div>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Audio format/bitrate ────────────────────────── */}
          {(activeTab === 'extract' || activeTab === 'audio') && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-4">
              <div>
                <p className="section-label">Output Format</p>
                <div className="flex flex-wrap gap-2">
                  {AUDIO_FORMATS.map((f) => (
                    <button key={f} onClick={() => setAudioFormat(f)} className={`format-chip ${audioFormat === f ? 'active' : ''}`}>
                      {f}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <p className="section-label">Bitrate / Quality</p>
                <div className="flex gap-2">
                  {BITRATES.map((b) => (
                    <button key={b} onClick={() => setAudioBitrate(b)} className={`quality-btn flex-1 ${audioBitrate === b ? 'active' : ''}`}>
                      {b} kbps
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          )}

          {/* ── Progress ────────────────────────────────────── */}
          <AnimatePresence>
            {converting && (
              <motion.div
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="glass-card rounded-2xl p-4 space-y-2"
              >
                <FunProgress percent={progress} tool="video" />
                {progStatus && (
                  <p className="text-xs text-center text-gray-500 animate-pulse">{progStatus}</p>
                )}
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── Alerts ──────────────────────────────────────── */}
          <AnimatePresence>
            {error && (
              <motion.div key="err" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="alert-error">
                <AlertCircle size={16} className="shrink-0 mt-0.5" />
                <p className="text-sm font-medium">{error}</p>
              </motion.div>
            )}
            {success && (
              <motion.div key="ok" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                className="alert-success">
                <CheckCircle size={16} className="shrink-0 mt-0.5" />
                <p className="text-sm font-medium">{success}</p>
              </motion.div>
            )}
          </AnimatePresence>

          {/* ── CTA buttons ─────────────────────────────────── */}
          <div className="space-y-3 pt-1">
            <Button
              onClick={handleConvert} size="lg" className="w-full"
              isLoading={converting} disabled={converting || !hasInput}
              leftIcon={<Zap size={18} />}
            >
              {converting ? 'Processing…' : (
                activeTab === 'extract' ? 'Extract Audio' :
                activeTab === 'audio'   ? 'Convert Audio' :
                videoMode === 'compress' ? 'Compress Video' :
                videoMode === 'trim'     ? 'Trim Video' :
                videoMode === 'enhance'  ? 'AI Enhance' :
                'Convert to GIF'
              )}
            </Button>

            <AnimatePresence>
              {downloadUrl && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0 }}
                >
                  <a href={downloadUrl} download className="block w-full">
                    <Button size="lg" variant="success" className="w-full" leftIcon={<Download size={18} />}>
                      Download Result
                    </Button>
                  </a>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default VideoConverter;
